/**
 * Interactive prompt helpers using @inquirer/prompts.
 *
 * These are only used in interactive (TTY) flows.
 * AI agents and CI should bypass prompts by passing all values via flags/env vars.
 */

import { select, input, password, confirm, checkbox, search } from '@inquirer/prompts'

export { select, input, password, confirm, checkbox, search }

/**
 * Prompt user to pick from a list of items.
 * Returns the value of the selected choice.
 */
export async function promptSelect<T>(
  message: string,
  choices: Array<{ name: string; value: T; description?: string }>
): Promise<T> {
  return select({ message, choices })
}

/**
 * Prompt user to pick multiple items from a list.
 * Returns array of selected values.
 */
export async function promptCheckbox<T>(
  message: string,
  choices: Array<{ name: string; value: T; checked?: boolean }>
): Promise<T[]> {
  return checkbox({ message, choices })
}

/**
 * Prompt user to search and pick from a large list.
 * Type-ahead fuzzy filtering — best for docs (could be hundreds).
 */
export async function promptSearch<T>(
  message: string,
  choices: Array<{ name: string; value: T; description?: string }>
): Promise<T> {
  return search({
    message,
    source: async (term) => {
      if (!term) return choices
      const lower = term.toLowerCase()
      return choices.filter((c) => c.name.toLowerCase().includes(lower))
    },
  })
}

/**
 * Prompt for text input. Returns trimmed string.
 */
export async function promptInput(message: string, required = true): Promise<string> {
  return input({
    message,
    validate: required ? (v) => (v.trim() ? true : 'Required') : undefined,
  })
}

/**
 * Prompt for a password (masked input).
 */
export async function promptPassword(message: string): Promise<string> {
  return password({
    message,
    mask: '*',
    validate: (v) => (v ? true : 'Required'),
  })
}

/**
 * Prompt for yes/no confirmation.
 */
export async function promptConfirm(message: string, defaultValue = true): Promise<boolean> {
  return confirm({ message, default: defaultValue })
}
